import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
    GitMerge,
    TrendingUp,
    ArrowLeftRight,
    RefreshCw,
    ChevronRight,
    ChevronLeft,
    Zap,
    AlertCircle
} from 'lucide-react';
import API_BASE_URL from '../config/api';

const BinaryIncome = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/binary_income.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.success) {
                setData(result.data);
            } else {
                setError(result.message || 'Failed to load binary income data');
            }
        } catch (err) {
            setError('Error loading data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const formatPV = (pv) => parseFloat(pv || 0).toFixed(2);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="p-8 text-center">
                    <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={fetchData}
                        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                        Retry
                    </button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-3 sm:p-4 md:p-6 max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <GitMerge className="text-indigo-600 flex-icon-fix" />
                            Binary Income
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">Extreme Left & Right PV matching income</p>
                    </div>
                    <button
                        onClick={fetchData}
                        className="p-2 sm:p-3 bg-indigo-100 text-indigo-600 rounded-xl hover:bg-indigo-200 transition-colors w-fit self-end sm:self-auto"
                    >
                        <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* Carry Forward Cards - PROMINENT */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8">
                    {/* Left Carry Forward */}
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 sm:p-6 text-white shadow-lg relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full translate-x-16 -translate-y-16"></div>
                        <div className="flex items-center justify-between mb-4 relative z-10">
                            <div className="p-2.5 bg-white/20 rounded-xl">
                                <ChevronLeft size={20} />
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-3 py-1 rounded-full">Left Side</span>
                        </div>
                        <div className="space-y-2 relative z-10">
                            <p className="text-blue-100 text-xs font-medium uppercase tracking-wider">Carry Forward PV</p>
                            <p className="text-3xl sm:text-4xl font-bold">{formatPV(data?.left_carry_forward)}</p>
                            <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                                <span className="text-xs text-blue-100/80">New Added:</span>
                                <span className="text-sm font-bold">{formatPV(data?.new_left_pv)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Right Carry Forward */}
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 sm:p-6 text-white shadow-lg relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full translate-x-16 -translate-y-16"></div>
                        <div className="flex items-center justify-between mb-4 relative z-10">
                            <div className="p-2.5 bg-white/20 rounded-xl">
                                <ChevronRight size={20} />
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-3 py-1 rounded-full">Right Side</span>
                        </div>
                        <div className="space-y-2 relative z-10">
                            <p className="text-purple-100 text-xs font-medium uppercase tracking-wider">Carry Forward PV</p>
                            <p className="text-3xl sm:text-4xl font-bold">{formatPV(data?.right_carry_forward)}</p>
                            <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                                <span className="text-xs text-purple-100/80">New Added:</span>
                                <span className="text-sm font-bold">{formatPV(data?.new_right_pv)}</span>
                            </div>
                        </div>
                    </div>
                </div>





                {/* PV Breakdown */}
                <div className="card-responsive mb-8">
                    <h3 className="text-md sm:text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <ArrowLeftRight className="text-indigo-600 flex-icon-fix" size={20} />
                        Cycle Performance
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-blue-50/50 rounded-2xl p-4 border border-blue-100">
                            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">Left Accumulation</p>
                            <p className="text-xl sm:text-2xl font-black text-blue-700 mt-1">{formatPV(data?.current_tree_left_pv)}</p>
                        </div>
                        <div className="bg-purple-50/50 rounded-2xl p-4 border border-purple-100">
                            <p className="text-[10px] font-bold text-purple-600 uppercase tracking-widest">Right Accumulation</p>
                            <p className="text-xl sm:text-2xl font-black text-purple-700 mt-1">{formatPV(data?.current_tree_right_pv)}</p>
                        </div>
                        <div className="bg-green-50/50 rounded-2xl p-4 border border-green-100">
                            <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest">Effective Left</p>
                            <p className="text-xl sm:text-2xl font-black text-green-700 mt-1">{formatPV(data?.effective_left_pv)}</p>
                            <p className="text-[10px] text-gray-400 mt-1 italic">Incl. Carry Forward</p>
                        </div>
                        <div className="bg-amber-50/50 rounded-2xl p-4 border border-amber-100">
                            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Effective Right</p>
                            <p className="text-xl sm:text-2xl font-black text-amber-700 mt-1">{formatPV(data?.effective_right_pv)}</p>
                            <p className="text-[10px] text-gray-400 mt-1 italic">Incl. Carry Forward</p>
                        </div>
                    </div>


                </div>

                {/* Total Earnings Highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
                    <div className="bg-emerald-600 rounded-2xl p-5 text-white shadow-lg relative overflow-hidden group">
                        <TrendingUp size={48} className="absolute -right-4 -bottom-4 text-white/10 group-hover:scale-110 transition-transform" />
                        <p className="text-[10px] font-bold text-emerald-100 uppercase tracking-widest mb-1">Total Earned</p>
                        <p className="text-2xl font-black">{formatCurrency(data?.total_payout_earned)}</p>
                    </div>
                    <div className="bg-indigo-600 rounded-2xl p-5 text-white shadow-lg relative overflow-hidden group">
                        <Zap size={48} className="absolute -right-4 -bottom-4 text-white/10 group-hover:scale-110 transition-transform" />
                        <p className="text-[10px] font-bold text-indigo-100 uppercase tracking-widest mb-1">Matching Bonus</p>
                        <p className="text-2xl font-black">{formatCurrency(data?.total_matching_income)}</p>
                    </div>
                    <div className="bg-gray-800 rounded-2xl p-5 text-white shadow-lg relative overflow-hidden group">
                        <RefreshCw size={48} className="absolute -right-4 -bottom-4 text-white/10 group-hover:scale-110 transition-transform" />
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Cycles</p>
                        <p className="text-2xl font-black">{data?.cycle_count || 0}</p>
                    </div>
                </div>

                {/* Payout History */}
                {data?.payout_history && data.payout_history.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="text-md sm:text-lg font-bold text-gray-900">Payout Log</h3>
                            <button className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest hover:underline">Download CSV</button>
                        </div>
                        <div className="table-responsive">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-400 text-[10px] uppercase font-bold tracking-widest">
                                    <tr>
                                        <th className="px-5 py-4 text-left">ID</th>
                                        <th className="px-5 py-4">L PV</th>
                                        <th className="px-5 py-4">R PV</th>
                                        <th className="px-5 py-4">Match</th>
                                        <th className="px-5 py-4">Income</th>
                                        <th className="px-5 py-4">Status</th>
                                        <th className="px-5 py-4 text-right">Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {data.payout_history.map((h, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-5 py-4 font-black text-gray-900">#{h.cycle_number}</td>
                                            <td className="px-5 py-4 text-center text-blue-600 font-medium">{formatPV(h.input_left_pv)}</td>
                                            <td className="px-5 py-4 text-center text-purple-600 font-medium">{formatPV(h.input_right_pv)}</td>
                                            <td className="px-5 py-4 text-center font-bold text-indigo-600">{formatPV(h.matching_pv)}</td>
                                            <td className="px-5 py-4 text-center font-black text-emerald-600">{formatCurrency(h.matching_income)}</td>
                                            <td className="px-5 py-4 text-center">
                                                <div className="flex flex-col items-center">
                                                    <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase ${h.status?.toLowerCase() === 'paid' || h.status?.toLowerCase() === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                        {h.status}
                                                    </span>
                                                    {parseFloat(h.performance_bonus || 0) > 0 && (
                                                        <span className="text-[8px] text-amber-600 font-black uppercase mt-1">Bonus Incl.</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 text-right text-gray-400 text-xs font-medium">
                                                {h.created_at ? new Date(h.created_at).toLocaleDateString('en-IN', {
                                                    day: '2-digit', month: 'short'
                                                }) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* FAQ / Info */}
                <div className="mt-8 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 border border-indigo-100 shadow-inner">
                    <h3 className="font-bold text-indigo-900 mb-4 flex items-center gap-2">
                        <Zap size={18} /> Quick Guide
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-white/50 p-3 rounded-xl">
                            <p className="text-[10px] font-black text-indigo-600 uppercase mb-1">Payout Logic</p>
                            <p className="text-xs text-indigo-900 leading-relaxed font-medium">Earn ₹40 per matching PV on your extreme paths. The higher side is carried forward to the next cycle.</p>
                        </div>
                        <div className="bg-white/50 p-3 rounded-xl">
                            <p className="text-[10px] font-black text-indigo-600 uppercase mb-1">Bonus Point</p>
                            <p className="text-xs text-indigo-900 leading-relaxed font-medium">Achieve 25 PV on Left and 50 PV on Right to unlock an additional ₹500 performance bonus.</p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default BinaryIncome;
